import type {
  HttpUserPrompt,
  PromptTextPart,
  PromptWandbRunPart,
} from '../client/promptParts';
import type {ThreadResponse, TurnResponse} from '../client/WbAgentClient';
import type {Thread} from './store/thread';

export const PROMPT_CONTEXT_RE = /^<PromptContext>[\s\S]*?<\/PromptContext>\s*/;
export const INLINE_RUN_ID_RE =
  /\{run_id: "[^"]*"(?:,\s*run_display_name: "([^"]*)")?\}/g;
const CONTEXT_RUN_RE =
  /\{run_id: "([^"]*)"(?:,\s*run_display_name: "([^"]*)")?\}/g;

export function decodePromptForThreadTitle(raw: HttpUserPrompt): string {
  let text: string;
  if (typeof raw === 'string') {
    text = raw;
  } else {
    // First text part marks user content per assemblePromptParts;
    // earlier parts are metadata (route context, attached run pills).
    const start = raw.findIndex(p => p.type === 'text');
    text = (start < 0 ? [] : raw.slice(start))
      .map(p => {
        if (p.type === 'text') {
          return (p as PromptTextPart).text;
        }
        if (p.type === 'wandb_run') {
          return `@${(p as PromptWandbRunPart).run_name}`;
        }
        return '';
      })
      .join(' ');
  }

  const cleaned = text
    .replace(PROMPT_CONTEXT_RE, '')
    .replace(INLINE_RUN_ID_RE, (_, displayName) =>
      displayName ? `@${displayName}` : ''
    )
    .replace(/  +/g, ' ')
    .trim();

  if (cleaned) {
    return cleaned;
  }
  // Fallback for historical threads that were created using the legacy API
  const contextMatch = text.match(PROMPT_CONTEXT_RE);
  if (contextMatch) {
    const contextNames: string[] = [];
    for (const m of contextMatch[0].matchAll(CONTEXT_RUN_RE)) {
      if (m[2]) {
        contextNames.push(m[2]);
      }
    }
    if (contextNames.length > 0) {
      return contextNames.map(name => `@${name}`).join(', ');
    }
  }
  return '';
}

/**
 * parseTurnTitle extracts a turn's stored title and prompt excerpt as the
 * (title, promptSnippet) pair used by the agent-chat store.
 */
export function parseTurnTitle(turn: Pick<TurnResponse, 'user_prompt'>): {
  title: string | null;
  promptSnippet: string | null;
} {
  const snippet = decodePromptForThreadTitle(turn.user_prompt ?? '');
  return {
    title: null,
    promptSnippet: snippet || null,
  };
}

/**
 * parseThreadTitle extracts a thread's stored title and server-derived prompt
 * preview as the (title, promptSnippet) pair used by the agent-chat store.
 */
export function parseThreadTitle(
  thread: Pick<ThreadResponse, 'title' | 'prompt_preview'>
): {title: string | null; promptSnippet: string | null} {
  const stored = thread.title?.trim() ?? '';
  const snippet = thread.prompt_preview?.trim() ?? '';
  return {
    title: stored || null,
    promptSnippet: snippet || null,
  };
}

/**
 * resolveThreadTitle returns the title to render for a chat history row.
 * Falls back through the stored title, the prompt snippet, and a generic
 * "Untitled conversation" label when both are empty.
 */
export function resolveThreadTitle(thread: Thread): string {
  return (
    thread.title?.trim() || thread.promptSnippet || 'Untitled conversation'
  );
}
